/**
 * @fileoverview Constants for the Coasting Detection DSP Pipeline
 *
 * These constants control the behavior of the Digital Signal Processing (DSP)
 * pipeline used to detect coasting periods in motorcycle racing telemetry.
 *
 * The values have been empirically tuned for GPS data sampled at ~5Hz with
 * typical GPS noise characteristics.
 */

/**
 * Distance bucket size in meters for spatial sampling.
 * Using 5m intervals provides good balance between resolution and noise reduction.
 */
export const DISTANCE_BUCKET_SIZE_METERS = 5

/**
 * Number of passes for acceleration smoothing.
 * Multiple passes provide better noise reduction while maintaining signal characteristics.
 */
export const SMOOTHING_PASSES = 2

/**
 * Gaussian weight decay factor for smoothing.
 * Controls how quickly weights decrease with distance from center point.
 */
export const GAUSSIAN_WEIGHT_DECAY = 0.3

/**
 * Window size for final intensity smoothing (in data points).
 * Larger window = smoother output but less responsive to rapid changes.
 */
export const FINAL_SMOOTHING_WINDOW_POINTS = 7

/**
 * Gaussian spread factor for final smoothing.
 * Controls the shape of the Gaussian kernel used for smoothing.
 */
export const FINAL_SMOOTHING_GAUSSIAN_SPREAD = 0.5

/**
 * Lean angle threshold (degrees) indicating traction limit.
 * Above this angle, rider is at traction limit - not coasting.
 */
export const TRACTION_LIMIT_LEAN_ANGLE_DEGREES = 42

/**
 * Combined g-force threshold indicating productive riding.
 * Above 0.73g combined lateral+longitudinal = pushing hard, not coasting.
 */
export const PRODUCTIVE_RIDING_G_FORCE = 0.73

/**
 * Hard braking threshold in m/s².
 * Braking harder than -6 m/s² (~0.6g) is always productive riding.
 */
export const HARD_BRAKING_THRESHOLD_MS2 = -6.0

/**
 * Hard acceleration threshold in m/s².
 * Acceleration above 3 m/s² is considered productive (full throttle).
 */
export const HARD_ACCELERATION_THRESHOLD_MS2 = 3.0

/**
 * Engine braking threshold in m/s².
 * Engine braking alone typically provides ~3 m/s² deceleration.
 */
export const ENGINE_BRAKE_THRESHOLD_MS2 = 3.0

/**
 * Threshold for detecting significant acceleration in m/s².
 * Used for transition detection (throttle-to-brake, brake-to-throttle).
 */
export const SIGNIFICANT_ACCELERATION_MS2 = 3.0

/**
 * Threshold for detecting significant braking in m/s².
 * Used for transition detection (throttle-to-brake, brake-to-throttle).
 */
export const SIGNIFICANT_BRAKING_MS2 = -3.0

/**
 * Moderate acceleration threshold in m/s².
 * Above 1.5 m/s² could be full throttle at high speeds.
 */
export const MODERATE_ACCELERATION_THRESHOLD_MS2 = 1.5

/**
 * High lean angle threshold for braking assessment (degrees).
 * Above 50°, even light braking is appropriate (at traction limit).
 */
export const HIGH_LEAN_ANGLE_FOR_BRAKING_DEGREES = 50

/**
 * Moderate lean angle threshold for braking assessment (degrees).
 * Below 35°, rider should be braking harder if trying to slow down.
 */
export const MODERATE_LEAN_ANGLE_FOR_BRAKING_DEGREES = 35

/**
 * Minimum peak intensity for a spike to be considered valid coasting.
 * Spikes with peak < 0.35 (35%) are considered GPS noise and removed.
 */
export const SPIKE_REMOVAL_MIN_PEAK_INTENSITY = 0.35

/**
 * Minimum duration (ms) for a coasting period to be considered valid.
 * Periods shorter than 600ms are too brief to be real coasting.
 */
export const SPIKE_REMOVAL_MIN_DURATION_MS = 600

/**
 * Maximum length (data points) for spike removal.
 * Sequences of 3 or fewer points can be spikes and are candidates for removal.
 */
export const SPIKE_REMOVAL_MAX_LENGTH_POINTS = 3

/**
 * Threshold for detecting isolated coasting points (intensity value).
 * Points above 0.3 surrounded by non-coasting = likely anomaly.
 */
export const ISOLATED_COASTING_THRESHOLD = 0.3

/**
 * Threshold for detecting significant coasting in context window.
 * Used to determine if surrounding points show real coasting pattern.
 */
export const SIGNIFICANT_COASTING_THRESHOLD = 0.2

/**
 * Window size (data points) for anomaly detection context.
 * Checks ±3 points around current point to detect isolated anomalies.
 */
export const ANOMALY_DETECTION_WINDOW_POINTS = 3

/**
 * Reduction factor when filling anomalous gaps.
 * Filled values are reduced by 0.7x to be conservative.
 */
export const ANOMALY_FILL_REDUCTION_FACTOR = 0.7

/**
 * Maximum gap length (data points) for interpolation.
 * Gaps of 1-3 points can be interpolated to create continuity.
 */
export const GAP_FILL_MAX_LENGTH_POINTS = 3

/**
 * Minimum surrounding intensity for gap filling.
 * Both sides of gap must have intensity ≥ 0.25 to trigger interpolation.
 */
export const GAP_FILL_MIN_SURROUNDING_INTENSITY = 0.25

/**
 * Threshold for classifying a point as "coasting" (intensity value).
 * Values below 0.15 (15%) are considered non-coasting.
 */
export const COASTING_DETECTION_THRESHOLD = 0.15

/**
 * Post-smoothing suppression threshold.
 * After smoothing, values < 0.1 are artifacts and set to zero.
 */
export const POST_SMOOTHING_SUPPRESSION_THRESHOLD = 0.1

/**
 * Threshold for determining if smoothed acceleration is near zero.
 * When smoothed acceleration is below this, the signal may have been crushed.
 */
export const NEAR_ZERO_ACCELERATION_THRESHOLD_MS2 = 0.8

/**
 * Minimum raw acceleration signal to be considered significant.
 * Used when smoothed signal is near zero to preserve actual acceleration/braking.
 */
export const MIN_RAW_ACCELERATION_SIGNAL_MS2 = 0.5
